import { getSupabaseClient } from '../lib/supabase';
import type { WebEvent } from '../types/events';
import type { WebMedia } from '../types/media';
import type { WebGuestInvite } from '../types/people';

export type WebManagementUsage = {
  event_id: string;
  host_user_id?: string | null;
  package_key?: string | null;
  package_display_name?: string | null;
  guest_count?: number | null;
  guest_limit?: number | null;
  photo_count?: number | null;
  photo_limit?: number | null;
  host_video_count?: number | null;
  effective_host_video_limit?: number | null;
  storage_bytes?: number | string | null;
  account_total_storage_bytes?: number | string | null;
  account_storage_plan_label?: string | null;
  account_storage_status?: string | null;
  needs_storage_subscription?: boolean | null;
  is_storage_billing_issue?: boolean | null;
  can_upgrade?: boolean | null;
  next_package_key?: string | null;
  next_package_display_name?: string | null;
};

export type EventBlastAudienceType =
  | 'all_invited'
  | 'selected_invites'
  | 'not_joined'
  | 'not_uploaded'
  | 'all_members';

export type EventBlastMessageType =
  | 'save_the_date'
  | 'event_reminder'
  | 'join_reminder'
  | 'upload_reminder';

export type WebEventBlastTemplate = {
  id: string;
  event_id: string;
  host_user_id: string;
  title: string;
  event_datetime_label: string | null;
  location_label: string | null;
  cover_photo_url: string | null;
  custom_message: string | null;
  theme_key: string;
  theme_color: string;
  include_qr_code: boolean;
  include_event_code: boolean;
  include_join_button: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type WebScheduledEventMessage = {
  id: string;
  event_id: string;
  template_id: string | null;
  created_by: string;
  message_type: EventBlastMessageType;
  scheduled_for: string;
  audience_type: EventBlastAudienceType;
  recipient_invite_ids: string[];
  recipient_user_ids: string[];
  channels: ('in_app' | 'push')[];
  status: 'draft' | 'scheduled' | 'processing' | 'sent' | 'canceled' | 'failed';
  title: string;
  body: string;
  data: Record<string, unknown>;
  sent_at: string | null;
  failure_reason: string | null;
  created_at: string;
  updated_at: string;
};

export type BulkInviteCandidate = {
  fullName: string;
  email: string;
};

export type BulkInviteResult = {
  created: number;
  skipped: number;
  failed: Array<{ email: string; reason: string }>;
};

const INVITE_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const DEFAULT_WEB_EVENT_BASE = 'https://join.wegathrd.com';

function cleanText(value: unknown): string {
  return String(value ?? '').trim();
}

function safeNumber(value: unknown): number {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizePermission(data: unknown): Record<string, unknown> | null {
  const value = Array.isArray(data) ? data[0] : data;
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

function canManage(permission: Record<string, unknown> | null): boolean {
  return Boolean(
    permission?.canManageSettings ||
      permission?.canManagePeople ||
      permission?.isOwner ||
      permission?.isAppAdmin,
  );
}

async function assertManagementAccess(eventId: string) {
  const client = getSupabaseClient();
  const { data, error } = await client.rpc('get_my_event_permission_summary', {
    p_event_id: eventId,
  });
  if (error) throw error;
  const permission = normalizePermission(data);
  if (!canManage(permission)) {
    throw new Error('Your event role does not include management access.');
  }
  return permission;
}

function generateInviteCode(): string {
  const bytes = new Uint32Array(8);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (value) => INVITE_CHARS[value % INVITE_CHARS.length]).join('');
}

export function formatManagementStorage(bytes: unknown): string {
  const value = Math.max(0, safeNumber(bytes));
  const gb = value / 1024 / 1024 / 1024;
  if (gb >= 1) return `${gb.toFixed(gb >= 10 ? 1 : 2)} GB`;
  const mb = value / 1024 / 1024;
  if (mb >= 1) return `${mb.toFixed(mb >= 10 ? 1 : 2)} MB`;
  return `${Math.round(value).toLocaleString()} bytes`;
}

export function formatManagementCount(value: unknown): string {
  return Math.max(0, Math.round(safeNumber(value))).toLocaleString();
}

export async function loadManagementUsage(eventId: string): Promise<WebManagementUsage | null> {
  const client = getSupabaseClient();
  const { data, error } = await client.rpc('get_manage_event_usage_dashboard', {
    p_event_id: eventId,
  });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return row && typeof row === 'object' ? (row as WebManagementUsage) : null;
}

export async function loadEventReactionCount(eventId: string): Promise<number> {
  const client = getSupabaseClient();
  const { count, error } = await client
    .from('photo_reactions')
    .select('id,photo:photos!inner(event_id)', { count: 'exact', head: true })
    .eq('photo.event_id', eventId);
  if (error) {
    console.warn('[web management] reaction count unavailable:', error.message);
    return 0;
  }
  return count ?? 0;
}

export async function loadEventAdminShareLink(event: WebEvent): Promise<string> {
  return `${DEFAULT_WEB_EVENT_BASE}/event/${encodeURIComponent(event.id)}`;
}

export async function setEventCoverMedia(
  eventId: string,
  media: WebMedia,
): Promise<{ coverMediaId: string | null; coverPhotoUrl: string | null }> {
  await assertManagementAccess(eventId);
  if (media.event_id !== eventId) throw new Error('This media item belongs to a different event.');
  if (media.media_type === 'video') throw new Error('Choose a photo for the event cover.');
  if (cleanText(media.moderation_status).toLowerCase() !== 'approved') {
    throw new Error('Only approved published photos can be used as the event cover.');
  }
  const client = getSupabaseClient();
  const { data, error } = await client.rpc('set_event_cover_media', {
    p_event_id: eventId,
    p_media_id: media.id,
  });
  if (error) throw error;
  const value = (Array.isArray(data) ? data[0] : data) as Record<string, unknown> | null;
  return {
    coverMediaId: cleanText(value?.coverMediaId ?? value?.cover_media_id) || media.id,
    coverPhotoUrl:
      cleanText(value?.coverPhotoUrl ?? value?.cover_photo_url) ||
      media.delivery?.display ||
      media.delivery?.thumb ||
      null,
  };
}

export async function clearEventCoverMedia(
  eventId: string,
): Promise<{ coverMediaId: null; coverPhotoUrl: string | null }> {
  await assertManagementAccess(eventId);
  const client = getSupabaseClient();
  const { data, error } = await client.rpc('clear_event_cover_media', {
    p_event_id: eventId,
  });
  if (error) throw error;
  const value = (Array.isArray(data) ? data[0] : data) as Record<string, unknown> | null;
  return {
    coverMediaId: null,
    coverPhotoUrl: cleanText(value?.coverPhotoUrl ?? value?.cover_photo_url) || null,
  };
}

export async function updateEventLocation(
  eventId: string,
  locationName: string,
  locationAddress: string,
): Promise<{ location_name: string | null; location_address: string | null }> {
  await assertManagementAccess(eventId);
  const patch = {
    location_name: locationName.trim() || null,
    location_address: locationAddress.trim() || null,
  };
  const client = getSupabaseClient();
  const { error } = await client.from('events').update(patch).eq('id', eventId);
  if (error) throw error;
  return patch;
}

export async function deleteEventMediaBatch(
  eventId: string,
  mediaIds: string[],
): Promise<{ requestedIds: string[]; deletedIds: string[]; failed: Array<{ id: string; reason: string }> }> {
  await assertManagementAccess(eventId);
  const requestedIds = Array.from(new Set(mediaIds.map(cleanText).filter(Boolean))).slice(0, 100);
  if (requestedIds.length === 0) {
    return { requestedIds: [], deletedIds: [], failed: [] };
  }
  const client = getSupabaseClient();
  const { data, error } = await client.rpc('delete_event_media_batch', {
    p_event_id: eventId,
    p_media_ids: requestedIds,
  });
  if (error) throw error;
  const payload = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>;
  const deletedIds = Array.isArray(payload.deletedIds)
    ? payload.deletedIds.map(cleanText).filter(Boolean)
    : Array.isArray(payload.deleted_ids)
      ? payload.deleted_ids.map(cleanText).filter(Boolean)
      : [];
  const failed = Array.isArray(payload.failed)
    ? (payload.failed as Array<Record<string, unknown>>)
        .map((item) => ({ id: cleanText(item.id), reason: cleanText(item.reason) || 'not_permitted_or_not_found' }))
        .filter((item) => item.id)
    : [];
  const deletedSet = new Set(deletedIds);
  const failedSet = new Set(failed.map((item) => item.id));
  for (const id of requestedIds) {
    if (!deletedSet.has(id) && !failedSet.has(id)) failed.push({ id, reason: 'not_permitted_or_not_found' });
  }
  return { requestedIds, deletedIds, failed };
}

export function parseBulkInviteText(value: string): BulkInviteCandidate[] {
  const byEmail = new Map<string, BulkInviteCandidate>();
  for (const rawLine of value.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;
    const parts = line
      .split(/[,;\t]/)
      .map((part) => part.trim().replace(/^['"]|['"]$/g, ''))
      .filter(Boolean);
    const emailIndex = parts.findIndex((part) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(part));
    if (emailIndex < 0) continue;
    const email = parts[emailIndex].toLowerCase();
    const fullName = parts.filter((_, index) => index !== emailIndex).join(' ').trim();
    byEmail.set(email, { fullName, email });
  }
  return [...byEmail.values()];
}

export async function bulkCreateGuestInvites(params: {
  event: WebEvent;
  inviterUserId: string;
  candidates: BulkInviteCandidate[];
}): Promise<BulkInviteResult> {
  await assertManagementAccess(params.event.id);
  const candidates = Array.from(new Map(params.candidates.map((item) => [item.email.toLowerCase(), item])).values());
  if (candidates.length === 0) return { created: 0, skipped: 0, failed: [] };
  if (candidates.length > 250) throw new Error('Import up to 250 guest email addresses at a time.');

  const client = getSupabaseClient();
  const { data: limitData, error: limitError } = await client.rpc('check_event_plan_limits', {
    p_event_id: params.event.id,
    p_add_photos: 0,
    p_add_videos: 0,
    p_add_guests: 0,
    p_add_invites: candidates.length,
    p_add_co_admins: 0,
    p_add_bytes: 0,
  });
  if (!limitError) {
    const check = (Array.isArray(limitData) ? limitData[0] : limitData) as Record<string, unknown> | null;
    if (check && check.allowed === false) {
      throw new Error(cleanText(check.reason) === 'invite_limit_reached'
        ? `${cleanText(check.plan_label) || 'This event package'} does not have room for ${candidates.length} more invites.`
        : 'The current event package does not allow this bulk invite import.');
    }
  }

  let created = 0;
  let skipped = 0;
  const failed: Array<{ email: string; reason: string }> = [];
  for (const candidate of candidates) {
    const row = {
      event_id: params.event.id,
      full_name: candidate.fullName,
      email: candidate.email.toLowerCase(),
      status: 'invited',
      role_to_assign: 'member',
      invite_code: generateInviteCode(),
      invited_by: params.inviterUserId,
      expires_at: params.event.expires_at ?? null,
    };
    const { data, error } = await client
      .from('guest_invites')
      .upsert(row, { onConflict: 'event_id,email' })
      .select('id')
      .single();
    if (error) {
      failed.push({ email: candidate.email, reason: error.message });
    } else if (data?.id) {
      created += 1;
    } else {
      skipped += 1;
    }
  }
  return { created, skipped, failed };
}

export function buildDefaultEventDateTimeLabel(event: WebEvent): string {
  const start = event.start_time || event.event_date;
  if (!start) return '';
  const startDate = new Date(start);
  if (Number.isNaN(startDate.getTime())) return '';
  const startLabel = startDate.toLocaleString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  if (!event.end_time) return startLabel;
  const endDate = new Date(event.end_time);
  if (Number.isNaN(endDate.getTime())) return startLabel;
  return `${startLabel} – ${endDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
}

export function buildDefaultLocationLabel(event: WebEvent): string {
  return [event.location_name, event.location_address].filter(Boolean).join(' · ');
}

export function buildDefaultBlastMessage(eventName: string): string {
  return `Save the date for ${eventName}! Join the event in Gathrd so everyone can share photos and videos in one place.`;
}

export async function loadEventBlastManagement(eventId: string): Promise<{
  template: WebEventBlastTemplate | null;
  invites: WebGuestInvite[];
  messages: WebScheduledEventMessage[];
}> {
  await assertManagementAccess(eventId);
  const client = getSupabaseClient();
  const [templateResult, invitesResult, messagesResult] = await Promise.all([
    client
      .from('event_blast_templates')
      .select('*')
      .eq('event_id', eventId)
      .eq('is_active', true)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle(),
    client.from('guest_invites').select('*').eq('event_id', eventId).order('invited_at', { ascending: false }),
    client.from('scheduled_event_messages').select('*').eq('event_id', eventId).order('scheduled_for', { ascending: false }).limit(25),
  ]);
  if (templateResult.error) throw templateResult.error;
  if (invitesResult.error) throw invitesResult.error;
  if (messagesResult.error) throw messagesResult.error;
  return {
    template: (templateResult.data as WebEventBlastTemplate | null) ?? null,
    invites: (invitesResult.data ?? []) as WebGuestInvite[],
    messages: (messagesResult.data ?? []) as WebScheduledEventMessage[],
  };
}

export async function saveEventBlastTemplate(input: {
  templateId?: string | null;
  eventId: string;
  hostUserId: string;
  title: string;
  eventDateTimeLabel: string;
  locationLabel: string;
  coverPhotoUrl?: string | null;
  customMessage: string;
  themeColor: string;
  includeQrCode: boolean;
  includeEventCode: boolean;
  includeJoinButton: boolean;
}): Promise<WebEventBlastTemplate> {
  await assertManagementAccess(input.eventId);
  const client = getSupabaseClient();
  const row = {
    event_id: input.eventId,
    host_user_id: input.hostUserId,
    title: input.title.trim(),
    event_datetime_label: input.eventDateTimeLabel.trim() || null,
    location_label: input.locationLabel.trim() || null,
    cover_photo_url: input.coverPhotoUrl?.trim() || null,
    custom_message: input.customMessage.trim() || null,
    theme_key: 'classic',
    theme_color: input.themeColor,
    include_qr_code: input.includeQrCode,
    include_event_code: input.includeEventCode,
    include_join_button: input.includeJoinButton,
    is_active: true,
  };
  if (!row.title) throw new Error('Enter a Save-the-Date title.');

  if (input.templateId) {
    const { data, error } = await client
      .from('event_blast_templates')
      .update(row)
      .eq('id', input.templateId)
      .select('*')
      .single();
    if (error) throw error;
    return data as WebEventBlastTemplate;
  }

  const { data: existing, error: existingError } = await client
    .from('event_blast_templates')
    .select('id')
    .eq('event_id', input.eventId)
    .eq('host_user_id', input.hostUserId)
    .eq('is_active', true)
    .maybeSingle();
  if (existingError) throw existingError;

  const existingId = cleanText((existing as { id?: string } | null)?.id);
  const query = existingId
    ? client.from('event_blast_templates').update(row).eq('id', existingId).select('*').single()
    : client.from('event_blast_templates').insert(row).select('*').single();
  const { data, error } = await query;
  if (error) throw error;
  return data as WebEventBlastTemplate;
}

export async function scheduleEventBlastMessage(input: {
  event: WebEvent;
  template: WebEventBlastTemplate;
  createdBy: string;
  messageType: EventBlastMessageType;
  scheduledFor: Date;
  audienceType: EventBlastAudienceType;
  selectedInviteIds?: string[];
}): Promise<WebScheduledEventMessage> {
  await assertManagementAccess(input.event.id);
  const eventName = input.event.name || 'your event';
  const title = input.messageType === 'save_the_date'
    ? `Save the date: ${eventName}`
    : input.messageType === 'upload_reminder'
      ? `Share your photos from ${eventName}`
      : `Reminder: ${eventName}`;
  const body = input.messageType === 'upload_reminder'
    ? 'Add your favorite photos and videos so everyone can relive the moment.'
    : input.messageType === 'join_reminder'
      ? 'Join the event in Gathrd so you can view details and contribute photos.'
      : input.template.custom_message || buildDefaultBlastMessage(eventName);

  const client = getSupabaseClient();
  const { data, error } = await client
    .from('scheduled_event_messages')
    .insert({
      event_id: input.event.id,
      template_id: input.template.id,
      created_by: input.createdBy,
      message_type: input.messageType,
      scheduled_for: input.scheduledFor.toISOString(),
      audience_type: input.audienceType,
      recipient_invite_ids: input.audienceType === 'selected_invites' ? input.selectedInviteIds ?? [] : [],
      recipient_user_ids: [],
      channels: ['in_app', 'push'],
      status: 'scheduled',
      title,
      body,
      data: {
        source: 'save_the_date_blast',
        templateId: input.template.id,
        joinLink: `gathrd://join-event?code=${encodeURIComponent(input.event.code || '')}`,
        eventCode: input.event.code,
      },
    })
    .select('*')
    .single();
  if (error) throw error;
  return data as WebScheduledEventMessage;
}

export async function processEventBlastMessageNow(messageId: string, eventId: string): Promise<void> {
  const client = getSupabaseClient();
  const { error } = await client.functions.invoke('process-scheduled-event-messages', {
    body: { messageId, eventId, limit: 1 },
  });
  if (error) throw error;
}
